import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import AuthService from '../../../api/auth';
import { ApiError, PaginationModel, handleError } from '../../../api/base';
import { PermissionsResponse } from '../../../api/permission';
import UserService from '../../../api/user';
import {
  PermissionModel,
  RoleModel,
  UserResponse,
} from '../../../api/user/reqres';
import { UserModel } from '../../../models/user';
import { ListRequestParams } from '../../../types/list';
import { convertParamsToQuery } from '../../../util';
import { RootState } from '../../index';

export interface UserState {
  value: UserModel[];
  user: UserModel | null;
  currentUser: UserModel | null;
  allUsers: UserModel[];
  fetchingUser: boolean;
  fetchingUsers: boolean;
  searchingUsers: boolean;
  savingUser: boolean;
  searchQuery: string;
  sort: string; // e.g., 'updated_at:desc'
  page: number;
  pageSize: number;
  error: {
    code: number | null;
    message: string | null;
  };
  pagination: PaginationModel | null;
  totalCount: number;
  userRoles: RoleModel[];
  userPermissions: PermissionModel[];
  fetchingRoles: boolean;
  fetchingPermissions: boolean;
}

interface CreateUserProps {
  user: UserModel;
  userRoles: string[];
}

interface UpdateUserProps {
  userId: string;
  user: UserModel;
  userRoles: string[];
}

interface SaveUserPermissionsProps {
  resp: UserResponse;
  userPermissions: string[];
}

interface DisableMfaProps {
  apiUser: string;
  apiPass: string;
}

interface UpdateUserClientProps {
  userId: string;
  clientId: string;
}

interface ChangePasswordProps {
  apiUser: string;
  apiPass: string;
  newPassword: string;
}

const initialState: UserState = {
  value: [],
  currentUser: null,
  user: null,
  allUsers: [],
  fetchingUser: false,
  fetchingUsers: false,
  searchQuery: '',
  searchingUsers: false,
  savingUser: false,
  error: { code: null, message: null },
  totalCount: 0,
  pagination: null,
  // stateful routing front-end defaults
  page: 1,
  pageSize: 20,
  sort: 'updated_at:desc',
  userRoles: [],
  userPermissions: [],
  fetchingRoles: false,
  fetchingPermissions: false,
};

// removed legacy usersParams in favor of params built from Redux state

export const saveUserPermissions = createAsyncThunk(
  'saveUserPermissions',
  async (
    { resp, userPermissions }: SaveUserPermissionsProps,
    { rejectWithValue }
  ) => {
    try {
      if (!userPermissions.length) return resp.data;
      const response: PermissionsResponse =
        await UserService().updateUserPermissions(
          resp.data.uuid,
          userPermissions
        );
      const updatedUserPermissions = response.data;
      const savedUser = {
        ...resp.data,
        permissions: updatedUserPermissions,
      };

      return savedUser;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getUser = createAsyncThunk(
  'users/getUser',
  async (userId: string, { rejectWithValue }) => {
    try {
      const params = {
        _columns: 'roles.display_name,roles.name,roles.id|asArray',
      };

      const query = convertParamsToQuery(params);
      const resp = await UserService().getUser(userId, query);

      return resp.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'users/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const resp = await UserService().getCurrentUser();

      return resp.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createUser = createAsyncThunk(
  'users/createUser',
  async (
    { user, userRoles }: CreateUserProps,
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const resp = await UserService().createUser({
        ...user,
        roles: userRoles,
      });

      const {
        users: { page, pageSize, searchQuery, sort },
      } = getState() as RootState;
      const params = {
        _limit: pageSize,
        _order_by: sort || 'updated_at:desc',
        _columns: 'roles.display_name,roles.name,roles.id|asArray',
        _offset: (page - 1) * pageSize,
        ...(searchQuery ? { name: `*${searchQuery.toLowerCase()}*` } : {}),
      };

      await dispatch(fetchUsers({ params }));

      return resp.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (userId: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await UserService().deleteUser(userId);

      const {
        users: { page, pageSize, searchQuery, sort },
      } = getState() as RootState;
      const params = {
        _limit: pageSize,
        _order_by: sort || 'updated_at:desc',
        _columns: 'roles.display_name,roles.name,roles.id|asArray',
        _offset: (page - 1) * pageSize,
        ...(searchQuery ? { name: `*${searchQuery.toLowerCase()}*` } : {}),
      };

      await dispatch(fetchUsers({ params }));

      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateUser = createAsyncThunk(
  'users/updateUser',
  async (
    { userId, user, userRoles }: UpdateUserProps,
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const resp = await UserService().updateUser(userId, {
        name: user.name,
        email: user.email,
        password: user.password,
        client_id: user.client_id,
        roles: userRoles,
      });

      dispatch(getCurrentUser());

      const {
        users: { page, pageSize, searchQuery, sort },
      } = getState() as RootState;
      const params = {
        _limit: pageSize,
        _order_by: sort || 'updated_at:desc',
        _columns: 'roles.display_name,roles.name,roles.id|asArray',
        _offset: (page - 1) * pageSize,
        ...(searchQuery ? { name: `*${searchQuery.toLowerCase()}*` } : {}),
      };
      dispatch(fetchUsers({ params }));

      return resp.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (
    {
      params,
      searching = false,
      fetchingUsersMore = false,
    }: {
      params: ListRequestParams;
      searching?: boolean;
      fetchingUsersMore?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const queryString = convertParamsToQuery(params);
      const resp = await UserService().listUsers(queryString);
      return {
        data: resp.data,
        pagination: resp.pagination,
        searching,
        fetchingUsersMore,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// removed legacy searchUsersByName in favor of params-based thunk

export const searchUsersByNameWithParams = createAsyncThunk(
  'users/searchUsersByNameWithParams',
  async ({ params }: { params: ListRequestParams }, { rejectWithValue }) => {
    try {
      const queryString = convertParamsToQuery(params);
      const resp = await UserService().listUsers(queryString);

      return {
        data: resp.data,
        pagination: resp.pagination,
        searching: false,
        fetchingUsersMore: false,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const paginateUsers = createAsyncThunk(
  'users/paginateUsers',
  async (
    { page, pageSize }: { page: number; pageSize: number },
    { rejectWithValue }
  ) => {
    try {
      const limit = pageSize;
      const nextOffset = (page - 1) * pageSize;

      const nextParams = {
        _limit: limit,
        _offset: nextOffset,
        _order_by: 'updated_at:desc',
        _columns: 'roles.display_name,roles.name,roles.id|asArray',
      };

      const query = convertParamsToQuery(nextParams);
      const resp = await UserService().listUsers(query);

      return {
        data: resp.data,
        pagination: resp.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const disableMfa = createAsyncThunk(
  'users/disableMfa',
  async ({ apiUser, apiPass }: DisableMfaProps, { rejectWithValue }) => {
    try {
      const response = await UserService().disableMfa(apiUser, apiPass);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateUserClient = createAsyncThunk(
  'users/updateUserClient',
  async (
    { userId, clientId }: UpdateUserClientProps,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const query = convertParamsToQuery({
        _columns: 'permissions.key|asArray,roles.*',
      });

      const resp = await UserService().updateUser(
        userId,
        { client_id: clientId },
        query
      );

      AuthService().saveUserToLocalStorage(resp.data);
      dispatch(getCurrentUser());

      return resp.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const changePassword = createAsyncThunk(
  'users/changePassword',
  async (
    { apiUser, apiPass, newPassword }: ChangePasswordProps,
    { rejectWithValue }
  ) => {
    try {
      const response = await UserService().changePassword(
        apiUser,
        apiPass,
        newPassword
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchUserRoles = createAsyncThunk(
  'users/fetchUserRoles',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await UserService().getUserRoles(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchUserPermissions = createAsyncThunk(
  'users/fetchUserPermissions',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await UserService().getUserPermissions(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    hydrateFromUrl: (
      state,
      {
        payload,
      }: {
        payload: {
          page?: number;
          pageSize?: number;
          search?: string;
          sort?: string;
        };
      }
    ) => {
      if (payload.page !== undefined) {
        state.page = payload.page;
      }
      if (payload.pageSize !== undefined) {
        state.pageSize = payload.pageSize;
      }
      if (payload.search !== undefined) {
        state.searchQuery = payload.search;
      }
      if (payload.sort !== undefined) {
        state.sort = payload.sort;
      }
    },
    setUserSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setUserPermissions: (state, action) => {
      state.userPermissions = action.payload;
    },
    setUserRoles: (state, action) => {
      state.userRoles = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCurrentUser.pending, (state) => {
        state.fetchingUser = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.fetchingUser = false;
        state.currentUser = action.payload;
        state.error = { code: null, message: null };

        AuthService().saveUserToLocalStorage(action.payload);
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.fetchingUser = false;
      });

    builder
      .addCase(getUser.pending, (state) => {
        state.fetchingUser = true;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.fetchingUser = false;
        state.user = action.payload;
        state.error = { code: null, message: null };
      })
      .addCase(getUser.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.fetchingUser = false;
      });

    builder
      .addCase(fetchUsers.pending, (state) => {
        state.fetchingUsers = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action: any) => {
        state.totalCount = action.payload.pagination.count;
        state.fetchingUsers = false;
        state.value = action.payload.data;
        state.pagination = action.payload.pagination;
        state.error = { code: null, message: null };
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.savingUser = false;
      });

    builder
      .addCase(createUser.pending, (state) => {
        state.savingUser = true;
      })
      .addCase(createUser.fulfilled, (state) => {
        state.savingUser = false;
        state.error = { code: null, message: null };
      })
      .addCase(createUser.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.savingUser = false;
      });

    builder
      .addCase(updateUser.pending, (state) => {
        state.savingUser = true;
      })
      .addCase(updateUser.fulfilled, (state) => {
        state.savingUser = false;
        state.error = { code: null, message: null };
      })
      .addCase(updateUser.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.savingUser = false;
      });

    builder
      .addCase(deleteUser.pending, (state) => {
        state.fetchingUsers = true;
      })
      .addCase(deleteUser.fulfilled, (state) => {
        state.fetchingUsers = false;
        state.error = { code: null, message: null };
      })
      .addCase(deleteUser.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.fetchingUsers = false;
      });

    builder
      .addCase(saveUserPermissions.pending, (state) => {
        state.savingUser = true;
      })
      .addCase(saveUserPermissions.fulfilled, (state) => {
        state.savingUser = false;
        state.error = { code: null, message: null };
      })
      .addCase(saveUserPermissions.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.savingUser = false;
      });

    // legacy search cases removed

    builder
      .addCase(searchUsersByNameWithParams.pending, (state) => {
        state.searchingUsers = true;
      })
      .addCase(searchUsersByNameWithParams.fulfilled, (state, action: any) => {
        state.searchingUsers = false;
        state.error = { code: null, message: null };
        if (action.payload) {
          state.value = action.payload.data;
          state.pagination = action.payload.pagination;
          state.totalCount = action.payload.pagination.count;
        }
      })
      .addCase(searchUsersByNameWithParams.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.searchingUsers = false;
      });

    builder
      .addCase(paginateUsers.pending, (state) => {
        state.fetchingUsers = true;
      })
      .addCase(paginateUsers.fulfilled, (state, action: any) => {
        state.fetchingUsers = false;
        state.error = { code: null, message: null };
        if (action.payload) {
          state.pagination = action.payload.pagination;
          state.value = [...action.payload.data];
        }
      })
      .addCase(paginateUsers.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.fetchingUsers = false;
      });

    builder
      .addCase(disableMfa.pending, (state) => {
        state.savingUser = true;
      })
      .addCase(disableMfa.fulfilled, (state) => {
        state.savingUser = false;
        state.error = { code: null, message: null };
      })
      .addCase(disableMfa.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.savingUser = false;
      });

    builder
      .addCase(changePassword.pending, (state) => {
        state.savingUser = true;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.savingUser = false;
        state.error = { code: null, message: null };
      })
      .addCase(changePassword.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.savingUser = false;
      });

    builder
      .addCase(fetchUserRoles.pending, (state) => {
        state.fetchingRoles = true;
      })
      .addCase(fetchUserRoles.fulfilled, (state, action) => {
        state.fetchingRoles = false;
        state.userRoles = action.payload;
        state.error = { code: null, message: null };
      })
      .addCase(fetchUserRoles.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.fetchingRoles = false;
      });

    builder
      .addCase(fetchUserPermissions.pending, (state) => {
        state.fetchingPermissions = true;
      })
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        state.fetchingPermissions = false;
        state.userPermissions = action.payload;
        state.error = { code: null, message: null };
      })
      .addCase(fetchUserPermissions.rejected, (state, action) => {
        if (action.error.message) {
          if ((action.payload as unknown as ApiError).error) {
            state.error = (action.payload as unknown as ApiError).error;
            handleError(action.payload as unknown as ApiError);
          }
        }
        state.fetchingPermissions = false;
      });
  },
});

export const { setUserSearchQuery, setUserPermissions, setUserRoles } =
  usersSlice.actions;
export const { hydrateFromUrl } = usersSlice.actions;

export const getUsers = (state: RootState) => state.users.value;
export const getFetchingUsers = (state: RootState) => state.users.fetchingUsers;
export const getFetchingUsersError = (state: RootState) => state.users.error;
export const getTotalCount = (state: RootState) => state.users.totalCount;
export const getPagination = (state: RootState) => state.users.pagination;
export const getSearchingUsersByName = (state: RootState) =>
  state.users.searchingUsers;
export const getSavingUser = (state: RootState) => state.users.savingUser;
export const getAllUsers = (state: RootState) => state.users.allUsers;
export const getUserRoles = (state: RootState) => state.users.userRoles;
export const getUserPermissions = (state: RootState) =>
  state.users.userPermissions;
export const getFetchingRoles = (state: RootState) => state.users.fetchingRoles;
export const getFetchingPermissions = (state: RootState) =>
  state.users.fetchingPermissions;

export default usersSlice.reducer;
